import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import services from "../../services/services";

type Rate = { currency: string; rateToUSD: string | number; updatedAt: string; note: string | null };

const CurrencyRates = () => {
  const { adminPermission } = useSelector((state: any) => state.counter);
  const [rates, setRates] = useState<Rate[]>([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState<Record<string, { rate: string; note: string }>>({});
  const [saving, setSaving] = useState<Record<string, boolean>>({});

  const load = async () => {
    setLoading(true);
    try {
      const res: any = await services.getCurrencyRates();
      setRates(res?.data?.data || []);
    } catch (e) {
      console.error("Failed to load rates:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (adminPermission?.is_admin == 2 && !adminPermission?.currencyRates) {
    return (
      <h1 className="text-3xl font-openSans font-bold text-aqua items-center flex justify-center h-[80vh]">
        You don't have permission
      </h1>
    );
  }

  const beginEdit = (r: Rate) => {
    setEditing((e) => ({ ...e, [r.currency]: { rate: String(r.rateToUSD), note: r.note || "" } }));
  };
  const cancelEdit = (code: string) => {
    setEditing((e) => { const next = { ...e }; delete next[code]; return next; });
  };
  const save = async (code: string) => {
    const edit = editing[code];
    if (!edit) return;
    const rateNum = parseFloat(edit.rate);
    if (!Number.isFinite(rateNum) || rateNum <= 0) {
      toast.error("Rate must be a positive number");
      return;
    }
    setSaving((s) => ({ ...s, [code]: true }));
    try {
      await services.updateCurrencyRate(code, { rateToUSD: rateNum, note: edit.note });
      toast.success(`${code} rate updated`);
      cancelEdit(code);
      await load();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Update failed");
    } finally {
      setSaving((s) => { const n = { ...s }; delete n[code]; return n; });
    }
  };

  return (
    <div className="w-full px-12 py-10">
      <div className="flex justify-between items-start mb-6">
        <div>
          <h4 className="font-sans font-bold text-2xl text-dimblack">Currency Rates</h4>
          <p className="text-sm text-lightgray mt-1 max-w-2xl">
            Used for converting non-USD IAP + booking amounts to USD on the dashboard. Values are
            frozen at transaction time — editing a rate here only affects transactions going forward,
            not historical totals.
          </p>
        </div>
        <button
          onClick={load}
          className="px-4 py-2 text-sm rounded-md border border-aqua text-aqua hover:bg-aqua hover:text-white transition"
        >
          Refresh
        </button>
      </div>

      {loading ? (
        <p className="text-aqua">Loading…</p>
      ) : (
        <div className="bg-white border border-aqua/30 rounded-2xl overflow-hidden shadow-sm">
          <table className="w-full text-sm text-left">
            <thead className="bg-aqua/10 text-xs uppercase tracking-wider text-dimblack">
              <tr>
                <th className="p-3">Currency</th>
                <th className="p-3">Rate to USD</th>
                <th className="p-3">Last Updated</th>
                <th className="p-3">Note</th>
                <th className="p-3 w-40">Action</th>
              </tr>
            </thead>
            <tbody>
              {rates.map((r) => {
                const edit = editing[r.currency];
                const isSaving = !!saving[r.currency];
                return (
                  <tr key={r.currency} className="border-t border-aqua/20">
                    <td className="p-3 font-mono font-bold">{r.currency}</td>
                    <td className="p-3">
                      {edit ? (
                        <input
                          type="number"
                          step="0.00000001"
                          className="border border-aqua/40 rounded px-2 py-1 w-40 outline-none focus:border-aqua"
                          value={edit.rate}
                          onChange={(e) => setEditing((s) => ({ ...s, [r.currency]: { ...s[r.currency], rate: e.target.value } }))}
                        />
                      ) : (
                        <span className="font-mono">{Number(r.rateToUSD).toFixed(8)}</span>
                      )}
                      {!edit && r.currency !== "USD" && (
                        <span className="ml-3 text-xs text-lightgray">
                          1 {r.currency} ≈ ${Number(r.rateToUSD).toFixed(4)} · $1 ≈ {(1 / Number(r.rateToUSD)).toFixed(2)} {r.currency}
                        </span>
                      )}
                    </td>
                    <td className="p-3 text-xs text-lightgray whitespace-nowrap">
                      {r.updatedAt ? new Date(r.updatedAt).toLocaleString() : "—"}
                    </td>
                    <td className="p-3 text-xs">
                      {edit ? (
                        <input
                          type="text"
                          className="border border-aqua/40 rounded px-2 py-1 w-full outline-none focus:border-aqua"
                          value={edit.note}
                          onChange={(e) => setEditing((s) => ({ ...s, [r.currency]: { ...s[r.currency], note: e.target.value } }))}
                          placeholder="Optional note"
                        />
                      ) : (
                        r.note || <span className="text-lightgray">—</span>
                      )}
                    </td>
                    <td className="p-3">
                      {edit ? (
                        <div className="flex gap-2">
                          <button
                            disabled={isSaving}
                            onClick={() => save(r.currency)}
                            className="px-3 py-1 text-xs rounded bg-aqua text-white disabled:opacity-60"
                          >
                            {isSaving ? "Saving…" : "Save"}
                          </button>
                          <button
                            disabled={isSaving}
                            onClick={() => cancelEdit(r.currency)}
                            className="px-3 py-1 text-xs rounded border border-gray-300 text-gray-700"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => beginEdit(r)}
                          className="px-3 py-1 text-xs rounded border border-aqua text-aqua hover:bg-aqua hover:text-white transition"
                        >
                          Edit
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default CurrencyRates;
